'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { AddToBasketLayerFragmentFragmentDoc } from '@packages/gql/generated/shopping/AddToBasketLayerFragmentFragmentDoc';
import type { LineItemFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { Suspense } from 'react';
import { unmask } from '@packages/gql/src/betterMasking';
import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { AddedProduct } from '@packages/modules/src/AddToBasketLayer/AddedProduct/AddedProduct.client';
import { AddedProductLoadingFallback } from '@packages/modules/src/AddToBasketLayer/AddedProduct/AddedProductLoadingFallback/AddedProductLoadingFallback.client';
import { AddToBasketRecommendations } from '../AddToBasketRecommendations';
import { AddToBasketLayerErrorFallback } from './AddToBasketLayerErrorFallback/AddToBasketLayerErrorFallback.client';
import { TaxHint } from '../TaxHint/TaxHint.client';

/* GraphQL */ `
  fragment AddToBasketLayerFragment on Product {
    delivery {
      ...TaxHintFragment
    }
    ...AddToBasketRecommendations
  }
`;

export type AddToBasketLayerProps = {
  lineItem: LineItemFragmentFragment;
  data: FragmentType<typeof AddToBasketLayerFragmentFragmentDoc>;
};

export const AddToBasketLayer = ({ lineItem, data }: AddToBasketLayerProps) => {
  const unmaskedData = unmask<typeof AddToBasketLayerFragmentFragmentDoc>(data);

  return (
    <div className="bg-color-grey-light flex grow flex-col gap-2 p-1.5 xl:p-3">
      <Card>
        <CardContent className="pb-2">
          <ErrorBoundary fallback={(reset) => <AddToBasketLayerErrorFallback reset={reset} />}>
            <Suspense fallback={<AddedProductLoadingFallback />}>
              <AddedProduct
                lineItem={lineItem}
                taxHintSlot={<TaxHint data={unmaskedData.delivery} />}
              />
            </Suspense>
          </ErrorBoundary>
        </CardContent>
      </Card>

      <AddToBasketRecommendations data={unmaskedData} />
    </div>
  );
};
